import {
    createTheme,
    Theme,
    ThemeOptions,
    ThemeProvider as MuiThemeProvider
} from '@mui/material/styles';
import * as React from 'react';

import { loadMaterialSymbols } from 'ml-rubiks-styleguide/src/icon';
import { initRubiksLicense } from 'ml-rubiks-styleguide/src/license';
import {
    getTheme,
    RubiksCssBaseline,
    ThemeType,
    ThemeTypeProvider
} from 'ml-rubiks-styleguide/src/theme';

import { PaymentStyling } from './types/config';

const INHERIT_FONT_FAMILY = 'inherit';

function convertStylingToThemeOverrides(
    styling?: PaymentStyling
): ThemeOptions {
    const borderRadiusValue = styling?.borderRadius
        ? parseInt(styling.borderRadius, 10)
        : undefined;

    return {
        typography: {
            h1: { fontFamily: INHERIT_FONT_FAMILY },
            h2: { fontFamily: INHERIT_FONT_FAMILY },
            h3: { fontFamily: INHERIT_FONT_FAMILY },
            h4: { fontFamily: INHERIT_FONT_FAMILY },
            h5: { fontFamily: INHERIT_FONT_FAMILY },
            h6: { fontFamily: INHERIT_FONT_FAMILY },
            subtitle1: { fontFamily: INHERIT_FONT_FAMILY },
            subtitle2: { fontFamily: INHERIT_FONT_FAMILY },
            body1: { fontFamily: INHERIT_FONT_FAMILY },
            body2: { fontFamily: INHERIT_FONT_FAMILY },
            button: { fontFamily: INHERIT_FONT_FAMILY },
            caption: { fontFamily: INHERIT_FONT_FAMILY },
            overline: { fontFamily: INHERIT_FONT_FAMILY }
        },
        palette: {
            ...(styling?.primaryColor && {
                primary: { main: styling.primaryColor }
            }),
            ...(styling?.secondaryColor && {
                secondary: { main: styling.secondaryColor }
            }),
            ...((styling?.textColorMain || styling?.textColorSecondary) && {
                text: {
                    ...(styling?.textColorMain && {
                        primary: styling.textColorMain
                    }),
                    ...(styling?.textColorSecondary && {
                        secondary: styling.textColorSecondary
                    })
                }
            }),
            ...(styling?.borderColor && { divider: styling.borderColor }),
            ...(styling?.backgroundColor && {
                background: {
                    default: styling.backgroundColor,
                    paper: styling.backgroundColor
                }
            }),
            ...((styling?.textColorSecondary || styling?.primaryColor) && {
                action: {
                    ...(styling?.textColorSecondary && {
                        disabled: styling.textColorSecondary
                    }),
                    ...(styling?.primaryColor && {
                        disabledBackground: `${styling.primaryColor}1F`
                    })
                }
            })
        },
        ...(borderRadiusValue && {
            shape: { borderRadius: borderRadiusValue }
        }),
        components: {
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        '&.Mui-disabled': {
                            color: styling?.textColorMain ?? 'inherit',
                            WebkitTextFillColor:
                                styling?.textColorMain ?? 'inherit'
                        }
                    },
                    input: {
                        '&.Mui-disabled': {
                            color: styling?.textColorMain ?? 'inherit',
                            WebkitTextFillColor:
                                styling?.textColorMain ?? 'inherit'
                        }
                    }
                }
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        '&.Mui-disabled': {
                            color: styling?.textColorSecondary ?? 'inherit'
                        }
                    }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                            borderColor: styling?.borderColor ?? 'inherit'
                        }
                    }
                }
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        '&.Mui-disabled': {
                            color: styling?.textColorSecondary ?? 'inherit'
                        }
                    }
                }
            }
        }
    };
}

function createCustomTheme(baseTheme: Theme, styling?: PaymentStyling): Theme {
    const overrides = convertStylingToThemeOverrides(styling);
    return createTheme(baseTheme, overrides);
}

interface BootstrapProps {
    children: React.ReactNode;
    themeType?: ThemeType;
    styling?: PaymentStyling;
}

export const Bootstrap = React.memo((props: BootstrapProps) => {
    const { themeType = 'magicline', styling } = props;

    const baseTheme = React.useMemo(() => getTheme(themeType), [themeType]);
    const theme = React.useMemo(
        () => createCustomTheme(baseTheme, styling),
        [baseTheme, styling]
    );

    React.useEffect(() => {
        initRubiksLicense();
        void loadMaterialSymbols();
    }, []);

    return (
        <MuiThemeProvider theme={theme}>
            <RubiksCssBaseline sx={{ fontFamily: INHERIT_FONT_FAMILY }}>
                <ThemeTypeProvider value={{ themeType }}>
                    {props.children}
                </ThemeTypeProvider>
            </RubiksCssBaseline>
        </MuiThemeProvider>
    );
});
